import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, TrendingUp, Shield, Calendar } from 'lucide-react';
import { useLanguage } from '@/hooks/use-language';

interface MemberAnalyticsProps {
  totalMembers: number;
  adminCount: number;
  groupDistribution: Record<string, number>;
  recentGrowth: {
    thisMonth: number;
    lastMonth: number;
  };
  contactComplete: number;
  missingEmail: number;
  missingPhone: number;
}

export function MemberAnalytics({
  totalMembers,
  adminCount,
  groupDistribution,
  recentGrowth,
  contactComplete,
  missingEmail,
  missingPhone,
}: MemberAnalyticsProps) {
  const { t } = useLanguage();
  
  const growthPercent = recentGrowth.lastMonth > 0 
    ? Math.round(((recentGrowth.thisMonth - recentGrowth.lastMonth) / recentGrowth.lastMonth) * 100)
    : 0;

  const topGroups = Object.entries(groupDistribution)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const contactCoverage = totalMembers > 0
    ? Math.round((contactComplete / totalMembers) * 100)
    : 0;

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t("members.count.members")}
          </CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalMembers}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {recentGrowth.thisMonth} {t("admin.this_month")}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t("members.admins")}
          </CardTitle>
          <Shield className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{adminCount}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {Math.round((adminCount / totalMembers) * 100)}% {t("members.of_total")}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t("members.growth")}
          </CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {growthPercent > 0 ? '+' : ''}{growthPercent}%
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            vs {t("members.last_month")}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t("groups.title")}
          </CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{Object.keys(groupDistribution).length}</div>
          <div className="flex flex-wrap gap-1 mt-2">
            {topGroups.slice(0, 2).map(([group, count]) => (
              <Badge key={group} variant="secondary" className="text-xs">
                {group}: {count}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="md:col-span-2 xl:col-span-1">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('members.contact_completeness', { defaultValue: 'Contact completeness' })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{contactCoverage}%</div>
          <p className="text-xs text-muted-foreground mt-1">
            {t('members.complete_contact', { defaultValue: 'Members with email & phone' })}
          </p>
          <div className="flex flex-col mt-3 space-y-1 text-xs text-muted-foreground">
            <span>{t('members.missing_email', { defaultValue: 'Missing email' })}: {missingEmail}</span>
            <span>{t('members.missing_phone', { defaultValue: 'Missing phone' })}: {missingPhone}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
